import React, { useState } from "react";
import { ExternalLink, Volume2 } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { toast } from "sonner";

const devopsTools = [
  {
    name: "DAG Health Monitor",
    url: "https://dag-health-monitor.emergent.host/",
    description: "Monitor and track DAG health status"
  },
  {
    name: "EFK / Kibana Dashboard",
    url: "https://kibana-demo.preview.emergentagent.com/",
    description: "Elasticsearch, Fluentd, Kibana logging stack"
  },
  {
    name: "Deploy / Release Dashboard",
    url: "https://deploy-dashboard-7.preview.emergentagent.com/",
    description: "Track deployments and releases"
  },
  {
    name: "Play with Kubernetes Lab",
    url: "https://labs.play-with-k8s.com/",
    description: "Interactive Kubernetes playground"
  },
  {
    name: "Infra Drift Monitor",
    url: "https://infra-drift-monitor.preview.emergentagent.com/",
    description: "Monitor infrastructure configuration drift"
  },
  {
    name: "OpenShift",
    url: "https://openshift-observe.preview.emergentagent.com/",
    description: "OpenShift observability and monitoring"
  },
  {
    name: "Swimlane",
    url: "https://stage-timeline-1.preview.emergentagent.com/",
    description: "Visual timeline and workflow management"
  },
  {
    name: "Terraform",
    url: "https://terraform-web-build.preview.emergentagent.com/",
    description: "Infrastructure as Code management",
    speechifyUrl: "https://speechify.app.link/DYzP0oKst2b"
  },
  {
    name: "CICD Dashboard",
    url: "https://deployment-health-1.preview.emergentagent.com/",
    description: "CI/CD pipeline monitoring"
  },
  {
    name: "Kubernetes K9S Cluster",
    url: "https://local-cluster-ui.preview.emergentagent.com/",
    description: "Kubernetes cluster management UI"
  },
  {
    name: "GitHub SYNK",
    url: "https://github-auto-push.preview.emergentagent.com/",
    description: "Automated GitHub synchronization"
  }
];

export default function DevopsTools() {
  const [speaking, setSpeaking] = useState(null);

  const handleListen = (e, tool, index) => {
    e.preventDefault();
    e.stopPropagation();

    // If the tool has a custom Speechify URL, open it in a new tab
    if (tool.speechifyUrl) {
      window.open(tool.speechifyUrl, '_blank', 'noopener,noreferrer');
      return;
    }
    
    // Stop any current speech
    if (speaking !== null) {
      window.speechSynthesis.cancel();
      if (speaking === index) {
        setSpeaking(null);
        return;
      }
    }

    // Check if browser supports speech synthesis
    if (!('speechSynthesis' in window)) {
      toast.error("Text-to-speech is not supported in your browser");
      return;
    }

    const textToSpeak = `${tool.name}. ${tool.description}`;
    const utterance = new SpeechSynthesisUtterance(textToSpeak);
    
    utterance.rate = 0.9;
    utterance.pitch = 1.0;
    utterance.volume = 1.0;
    
    utterance.onstart = () => {
      setSpeaking(index);
    };
    
    utterance.onend = () => {
      setSpeaking(null);
    };
    
    utterance.onerror = () => {
      setSpeaking(null);
      toast.error("Failed to play audio");
    };

    window.speechSynthesis.speak(utterance);
  };

  return (
    <div className="min-h-screen bg-[#EDF1FB]">
      <Navbar variant="solid" />
      
      {/* Hero Section */}
      <section className="relative overflow-hidden nst-navy text-white pt-[120px] pb-16">
        <div className="absolute inset-0 bg-grid-dark opacity-40" />
        <div className="glow-orb w-[420px] h-[420px] -top-20 -left-20" style={{ background: "#2E6BF6" }} />
        <div className="glow-orb w-[520px] h-[520px] top-20 -right-32" style={{ background: "#16C47F" }} />
        
        <div className="relative max-w-[1280px] mx-auto px-6 lg:px-10">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold leading-tight">
              Nanoscience Cloud Computing
            </h1>
            <p className="mt-6 text-lg text-white/80 leading-relaxed">
              Access our comprehensive suite of DevOps tools and monitoring dashboards. Built for platform engineers and cloud architects.
            </p>
          </div>
        </div>
      </section>

      {/* DEVOPS Lab Section */}
      <section className="py-20 lg:py-28">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
          <div className="mb-12">
            <h2 className="text-3xl md:text-4xl font-extrabold text-[#0A1F47]">
              DEVOPS Lab
            </h2>
            <p className="mt-4 text-base text-[#0A1F47]/70 max-w-2xl">
              Explore our collection of DevOps tools, dashboards, and interactive labs. Each tool is designed to enhance your cloud infrastructure monitoring and management capabilities.
            </p>
          </div>

          {/* Tools Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {devopsTools.map((tool, index) => (
              <div
                key={index}
                className="group bg-white rounded-xl p-6 border border-[#E2E8F4] hover:border-[#2E6BF6] hover:shadow-lg transition-all duration-300 relative"
              >
                <div className="flex items-start justify-between mb-3">
                  <h3 className="text-lg font-bold text-[#0A1F47] group-hover:text-[#2E6BF6] transition-colors">
                    {tool.name}
                  </h3>
                  <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                    <button
                      onClick={(e) => handleListen(e, tool, index)}
                      data-testid={`listen-btn-${tool.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                      className={`p-2 rounded-lg transition-all ${
                        speaking === index 
                          ? 'bg-[#2E6BF6] text-white' 
                          : 'hover:bg-[#F5F7FA] text-[#0A1F47]/60 hover:text-[#2E6BF6]'
                      }`}
                      title={tool.speechifyUrl ? "Listen on Speechify" : "Listen to description"}
                    >
                      <Volume2 className={`w-4 h-4 ${speaking === index ? 'animate-pulse' : ''}`} />
                    </button>
                    <a
                      href={tool.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 rounded-lg hover:bg-[#F5F7FA] transition-all"
                      title="Open tool"
                    >
                      <ExternalLink className="w-4 h-4 text-[#0A1F47]/60 hover:text-[#2E6BF6] transition-colors" />
                    </a>
                  </div>
                </div>
                <p className="text-sm text-[#0A1F47]/60 leading-relaxed">
                  {tool.description}
                </p>
              </div>
            ))}
          </div>

          {/* CTA Section */}
          <div className="mt-16 text-center">
            <div className="inline-block bg-[#F5F7FA] rounded-2xl p-8 md:p-10">
              <h3 className="text-2xl font-bold text-[#0A1F47] mb-3">
                Need Custom DevOps Solutions?
              </h3>
              <p className="text-base text-[#0A1F47]/70 mb-6 max-w-xl mx-auto">
                Our team can help you build custom monitoring dashboards, automation tools, and cloud infrastructure tailored to your needs.
              </p>
              <a
                href="/#contact"
                className="btn-navy inline-flex"
              >
                Contact Our Team
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
